/**
 * Per-organization Assistant AI configuration (`/api/runtime-ai/*`).
 *
 * Read/written by a functional administrator from the runtime "System
 * administration → AI" tab. The row is always keyed to the CALLER's own tenant
 * server-side, so a cross-tenant func-admin configures their organization's AI
 * and no one else's. The credential is never returned — only whether one exists.
 */
import api from './client'

/** One selectable model in a provider's (or an agent's) current line-up. */
export interface RuntimeAiModelOption {
  id: string
  label: string
  /** OpenCode only: a no-cost model — the one kind that may run without a key. */
  free?: boolean
}

export interface RuntimeAiProviderOption {
  id: string
  label: string
  models: RuntimeAiModelOption[]
}

/** Who pays for a run-time AI call. */
export type RuntimeAiFunding = 'own_key' | 'platform'

/**
 * How hard the model is asked to think, in one vocabulary the server maps onto
 * each provider (OpenAI `reasoning_effort`, Anthropic's extended-thinking
 * budget, Gemini's `thinkingBudget`, the agent CLIs' `--effort`).
 *
 * `none` is the default and the only value every model accepts — and it is not
 * the same as sending nothing: a reasoning model left unasked reasons anyway,
 * and OpenAI then refuses the assistant's function tools outright.
 */
export type RuntimeAiEffort = 'none' | 'low' | 'medium' | 'high' | 'xhigh'

/** One kind of run-time AI work that may get its own model / effort. */
export interface RuntimeAiTask {
  id: string
  label: string
  hint?: string
}

/** A task's own choices; a blank / missing value uses the main setting. */
export interface RuntimeAiTaskPick {
  model?: string
  effort?: RuntimeAiEffort | ''
}

export interface RuntimeAiConfigView {
  /** A usable configuration (engine + stored credential, or AI Credit) exists. */
  configured: boolean
  engine: string
  /** 'own_key' = this organization's stored credential; 'platform' = Twasta's
   *  key, charged to this organization's AI credits. */
  funding?: RuntimeAiFunding
  provider: string
  model: string
  /** Reasoning effort for this organization's assistant. Defaults to 'none'. */
  effort?: RuntimeAiEffort
  /** Model / effort per kind of work, keyed by `RuntimeAiTask.id`. */
  task_config?: Record<string, RuntimeAiTaskPick>
  /** The kinds of work an organization may tune (Advanced). */
  tasks?: RuntimeAiTask[]
  credential_configured: boolean
  updated_at: string | null
  /** The caller's organization owns the application — it already runs on the
   *  owner's Configure AI profile, so a row here is an override, not a must. */
  is_owner_tenant?: boolean
  /** The assistant cannot answer this organization until a row exists. */
  required?: boolean
  providers?: string[]
  engines?: string[]
  /** The reasoning efforts this backend understands, in order. */
  efforts?: RuntimeAiEffort[]
  /** The funding sources THIS caller may pick. AI Credit is offered to the
   *  organization that owns the application only. */
  fundings?: RuntimeAiFunding[]
  /** Whether this Twasta instance has a platform AI a run-time call can run on
   *  — a provider API key, or the platform's own connected agent subscription.
   *  Without one, AI Credit has nothing to run on. */
  platform_key_available?: boolean
  /** Provider line-up with its current models — drives the model dropdown so
   *  the console never asks an admin to type a model id from memory. */
  catalog?: RuntimeAiProviderOption[]
  /** What AI Credit runs on, and the models it may be pointed at. */
  platform_ai?: RuntimeAiPlatformView
  /** Models each agent CLI accepts, keyed by engine id. */
  agent_models?: Record<string, RuntimeAiModelOption[]>
  /** Owner tenant only: what the assistant falls back on with no row here —
   *  resolved through the same ladder the assistant itself runs, so the banner
   *  can never promise an AI profile the assistant then refuses to find.
   *  `available: null` means the server could not tell. */
  owner_fallback?: RuntimeAiOwnerFallback
  tenant_id?: string
  /** Document search: this organization's own embedding profile. */
  embedding?: RuntimeEmbeddingView
  /** Providers with an embeddings endpoint (OpenAI / Google today). */
  embedding_providers?: string[]
  /** `{provider: {model, dimensions}}` — what each is called with by default.
   *  Never a chat model: those 404 against the embeddings endpoint. */
  embedding_models?: Record<string, { model: string; dimensions: number }>
  /** Whether AI Credit can embed here — false on an instance whose platform AI
   *  is a Claude Code / Codex subscription, which has no embeddings endpoint. */
  embedding_platform_available?: boolean
  /** What AI Credit would embed with, named rather than merely offered. */
  embedding_platform?: { provider: string; model: string; dimensions: number }
  /** What serves while this organization chooses nothing. */
  embedding_fallback?: RuntimeEmbeddingFallback
  /** Image generation (pictures in Assistant-written content). */
  image?: RuntimeImageView
  image_providers?: string[]
  image_models?: Record<string, string[]>
  image_default_models?: Record<string, string>
  /** Whether AI Credit can make images here (the instance has a platform image key). */
  image_platform_available?: boolean
  /** What AI Credit would generate with, named rather than merely offered. */
  image_platform?: { provider: string; model: string }
}

/**
 * The instance's platform AI, as offered to an organization funding on credit.
 *
 * The provider is reported, never chosen: the credential belongs to the
 * instance and an instance administrator picked it, so a model from any other
 * provider could not be called with it. The MODEL is chosen here, because what
 * an end user's question costs in this organization's credits is this
 * organization's business.
 */
export interface RuntimeAiPlatformView {
  /** 'llm', or the agent CLI the platform default runs on. */
  engine: string
  provider: string
  /** What answers when no model is chosen — the instance admin's own default
   *  for the assistant, resolved the way a real question resolves it. */
  default_model: string
  models: RuntimeAiModelOption[]
}

/**
 * What answers the OWNER organization's users when it configures nothing here:
 * the builder's Configure AI profile, and only when that profile carries a
 * usable credential. AI Credit is NOT a fallback — it answers only where a
 * saved row chose it as its funding, so an organization that configures
 * nothing (or deletes what it had) spends no credits.
 */
export interface RuntimeAiOwnerFallback {
  /** True when something can actually answer; false when nothing can; null when
   *  the server could not resolve it. */
  available: boolean | null
  /** 'profile' = the builder's Configure AI profile. ('credit' is retained for
   *  older payloads; the server no longer resolves the fallback to it.) */
  source: '' | 'profile' | 'credit'
  /** That source named for a human, e.g. "Claude Code agent · claude-opus-4-8". */
  label: string
  /** Why nothing can answer — worded for the admin reading it. */
  message: string
}

/**
 * Where an organization's DOCUMENT TEXT is converted into vectors, and on whose
 * account — a second credential, independent of the assistant's above.
 *
 * Independent because only OpenAI and Google publish an embeddings endpoint: an
 * organization whose assistant answers on Anthropic, on a CLI agent or on AI
 * Credit has nothing to embed with until it names a provider here, and its
 * repositories are searched by keyword alone.
 *
 * `source: ''` means nothing is chosen and the project OWNER's Settings → AI
 * profile keeps serving — the author's vendor, on the author's account. That is
 * what `embedding_fallback` on the config view names, so an organization can
 * see it and decide.
 *
 * It changes nothing about WHAT a search may reach: the vectors live in this
 * organization's own database beside its own documents, and the provider is
 * handed one passage at a time and keeps nothing the application reads back.
 */
export type RuntimeEmbeddingSource = '' | 'own_key' | 'platform'

export interface RuntimeEmbeddingView {
  source: RuntimeEmbeddingSource
  provider: string
  /** The pinned model, '' when the provider's default is used. */
  model: string
  /** A self-hosted, OpenAI-compatible endpoint, or ''. */
  base_url: string
  /** Vector width — the number that must stay stable, or every repository
   *  needs re-indexing before search works properly again. */
  dimensions: number
  /** What actually runs: the pin, else the provider's embedding default. */
  effective_model: string
  credential_configured: boolean
  /** True when this profile can actually embed, not merely that one is stored. */
  configured: boolean
}

export interface RuntimeEmbeddingUpdate {
  source: RuntimeEmbeddingSource
  provider?: string
  model?: string
  base_url?: string
  dimensions?: number
  /** Omit to keep the stored credential; '' clears it. */
  api_key?: string
}

/** What embeds this organization's documents while it configures nothing:
 *  the application owner's own profile. `available: null` = unresolvable. */
export interface RuntimeEmbeddingFallback {
  available: boolean | null
  provider: string
  model: string
}

/** Result of the embedding "Test" — one real phrase converted to a vector. */
export interface RuntimeEmbeddingTestResult {
  ok: boolean
  error?: string | null
  message?: string | null
  source?: RuntimeEmbeddingSource
  provider?: string
  model?: string
  dimensions?: number
  used_stored_key?: boolean
}

/** Result of the "Test" button — a real one-token call to the selected model. */
export interface RuntimeAiTestResult {
  ok: boolean
  error?: string | null
  message?: string | null
  /** True when the stored credential was tested (the key field was blank). */
  used_stored_key?: boolean
  /** Agent engines only: whether that CLI is installed on the app's host. */
  cli_available?: boolean
  /** Which funding source was tested. */
  funding?: RuntimeAiFunding
  provider?: string
  model?: string
  effort?: RuntimeAiEffort
}

export interface RuntimeAiConfigUpdate {
  engine: string
  funding?: RuntimeAiFunding
  provider?: string
  model?: string
  effort?: RuntimeAiEffort
  /** Omit to keep the stored credential; '' clears it. */
  api_key?: string
  /** Omit to leave the document-search profile exactly as it is. The two forms
   *  share one Save, and changing an assistant model must never be the reason a
   *  repository stops being embedded. */
  embedding?: RuntimeEmbeddingUpdate
  /** Omitted → the stored image profile is left exactly as it is. */
  image?: RuntimeImageUpdate
  /** Model / effort per task. Omitted → kept; {} → every task on the main setting. */
  task_config?: Record<string, RuntimeAiTaskPick>
}

/** Image generation, chosen like embeddings: '' = nothing chosen (the
 *  application owner's Settings → AI image profile serves), 'own_key' = this
 *  organization's OpenAI / Google key, 'platform' = the instance's platform
 *  image key on this organization's AI credits. */
export type RuntimeImageSource = '' | 'own_key' | 'platform'

export interface RuntimeImageView {
  source: RuntimeImageSource
  provider: string
  model: string
  effective_model: string
  credential_configured: boolean
  configured: boolean
}

export interface RuntimeImageUpdate {
  source: RuntimeImageSource
  provider?: string
  model?: string
  /** Omit to keep the stored credential; '' clears it. */
  api_key?: string
}

export interface RuntimeImageTestResult {
  ok: boolean
  error?: string | null
  message?: string | null
  provider?: string
  model?: string
}

/** Check the image key reaches its model — a free lookup, no picture is made. */
export async function testRuntimeImage(
  ownerUserId: string, project: string, body: RuntimeImageUpdate,
): Promise<RuntimeImageTestResult> {
  const { data } = await api.post(
    `/runtime-ai/${encodeURIComponent(ownerUserId)}/${encodeURIComponent(project)}/test-image`,
    body)
  return data as RuntimeImageTestResult
}

export async function getRuntimeAiConfig(
  ownerUserId: string, project: string,
): Promise<RuntimeAiConfigView> {
  const { data } = await api.get(
    `/runtime-ai/${encodeURIComponent(ownerUserId)}/${encodeURIComponent(project)}`)
  return data as RuntimeAiConfigView
}

export async function saveRuntimeAiConfig(
  ownerUserId: string, project: string, body: RuntimeAiConfigUpdate,
): Promise<RuntimeAiConfigView> {
  const { data } = await api.put(
    `/runtime-ai/${encodeURIComponent(ownerUserId)}/${encodeURIComponent(project)}`, body)
  return data as RuntimeAiConfigView
}

export async function deleteRuntimeAiConfig(
  ownerUserId: string, project: string,
): Promise<boolean> {
  const { data } = await api.delete(
    `/runtime-ai/${encodeURIComponent(ownerUserId)}/${encodeURIComponent(project)}`)
  return !!data?.ok
}

/**
 * Verify the configuration against the provider before trusting it. Leaving
 * `api_key` blank tests the credential already stored server-side — the UI
 * never has the secret to resend.
 */
export async function testRuntimeAiConfig(
  ownerUserId: string, project: string, body: RuntimeAiConfigUpdate,
): Promise<RuntimeAiTestResult> {
  const { data } = await api.post(
    `/runtime-ai/${encodeURIComponent(ownerUserId)}/${encodeURIComponent(project)}/test`, body)
  return data as RuntimeAiTestResult
}

/**
 * Convert one phrase with the embedding profile as the form now stands.
 *
 * Worth a button of its own: a broken embedding profile raises no error anybody
 * sees. Documents still file and searches still return something — answers just
 * quietly get worse. This is the one moment that failure is cheap.
 */
export async function testRuntimeEmbedding(
  ownerUserId: string, project: string, body: RuntimeEmbeddingUpdate,
): Promise<RuntimeEmbeddingTestResult> {
  const { data } = await api.post(
    `/runtime-ai/${encodeURIComponent(ownerUserId)}/${encodeURIComponent(project)}/test-embedding`,
    body)
  return data as RuntimeEmbeddingTestResult
}

// ── Credits and consumption, for the func-admin running the application ────

/** One grouped row of AI consumption. `charged` is the real wallet outflow;
 *  `credits` prices an own-key row too, so the report is never blank for an
 *  organization paying its provider directly. */
export interface RuntimeUsageBucket {
  calls: number
  input_tokens: number
  output_tokens: number
  cache_read_tokens: number
  credits: number
  charged: number
}

export interface RuntimeUsageByUser extends RuntimeUsageBucket {
  user_key: string
  /** How the application knew them. A project user has no platform profile,
   *  so this is the only name there is. */
  user_name: string
}

export interface RuntimeUsageByFunction extends RuntimeUsageBucket {
  task_type: string
  /** The feature in the words of somebody running the app, not the logger's. */
  label: string
}

export interface RuntimeUsageByDay extends RuntimeUsageBucket { date: string }
export interface RuntimeUsageByModel extends RuntimeUsageBucket {
  provider: string
  model: string
}

export interface RuntimeUsageCall {
  at: string
  user_key: string
  user_name: string
  task_type: string
  label: string
  provider: string
  model: string
  input_tokens: number
  output_tokens: number
  credits: number
  /** False for a call served by this organization's own provider key. */
  on_credit: boolean
  duration_ms: number | null
}

export interface RuntimeUsageReport {
  project: string
  tenant_id: string
  from: string
  to: string
  totals: RuntimeUsageBucket
  by_user: RuntimeUsageByUser[]
  by_function: RuntimeUsageByFunction[]
  by_day: RuntimeUsageByDay[]
  by_model: RuntimeUsageByModel[]
  recent: RuntimeUsageCall[]
}

/** The AI-credit standing of the organization running this application. */
export interface RuntimeCreditsView {
  balance: number
  /** 'tenant' = the organization's shared wallet; 'user' = the owner's own,
   *  which the shared wallet falls back to when it is empty. */
  scope: string
  low_balance_threshold: number | null
  credit_billing_enabled: boolean
  /** Non-null when the assistant would refuse the next AI Credit question —
   *  the same 402-shaped payload, with the wording the end user would see. */
  blocked: { code?: string; detail?: string; balance?: number } | null
  /** Whether this application is actually funded by AI Credit. */
  on_credit: boolean
}

export async function getRuntimeCredits(
  ownerUserId: string, project: string,
): Promise<RuntimeCreditsView> {
  const { data } = await api.get(
    `/runtime-ai/${encodeURIComponent(ownerUserId)}/${encodeURIComponent(project)}/credits`)
  return data as RuntimeCreditsView
}

export async function getRuntimeUsage(
  ownerUserId: string, project: string,
  params: { days?: number; date_from?: string; date_to?: string } = {},
): Promise<RuntimeUsageReport> {
  const { data } = await api.get(
    `/runtime-ai/${encodeURIComponent(ownerUserId)}/${encodeURIComponent(project)}/usage`,
    { params })
  return data as RuntimeUsageReport
}
